import { AlertTriangle, ArrowRight, LoaderCircle, Podcast } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'arcway/lib/client'

import { Button } from '#components/ui/button.jsx'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '#components/ui/card.jsx'
import { ErrorAlert } from '#components/ui/error-alert.jsx'
import { Field, FieldDescription, FieldLabel } from '#components/ui/field.jsx'
import { Input } from '#components/ui/input.jsx'

export function importReturnPath(url) {
  const search = new URLSearchParams({ url })
  return `/account/import?${search}`
}

export function ImportPreview({ preview }) {
  return (
    <Card className='import-preview'>
      <CardHeader>
        <div className='import-preview-heading flex items-start gap-4'>
          <span className='import-show-icon flex size-11 shrink-0 items-center justify-center rounded-lg bg-muted'>
            <Podcast aria-hidden='true' className='size-5' />
          </span>
          <div className='min-w-0'>
            <CardTitle as='h2' className='break-words'>
              {preview.show.title}
            </CardTitle>
            <CardDescription className='mt-1'>
              {preview.show.author || 'Author not provided'} ·{' '}
              {preview.episodes.length} episode
              {preview.episodes.length === 1 ? '' : 's'} found
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className='space-y-5'>
        {preview.show.description && (
          <p className='line-clamp-3 text-sm leading-6 text-muted-foreground'>
            {preview.show.description}
          </p>
        )}
        {!preview.episodes.length && (
          <p className='text-sm text-muted-foreground'>
            No episodes were found in this feed.
          </p>
        )}
        {preview.episodes.length > 0 && (
          <div>
            <h3 className='text-sm font-medium'>Episodes in this feed</h3>
            <ul className='import-episodes mt-3 divide-y rounded-lg border px-4'>
              {preview.episodes.slice(0, 5).map((episode, index) => (
                <li
                  className='break-words py-3 text-sm'
                  key={`${episode.guid}-${index}`}
                >
                  {episode.title}
                </li>
              ))}
            </ul>
            {preview.episodes.length > 5 && (
              <p className='mt-2 text-xs text-muted-foreground'>
                And {preview.episodes.length - 5} more episode
                {preview.episodes.length - 5 === 1 ? '' : 's'}.
              </p>
            )}
          </div>
        )}
        {preview.warnings.length > 0 && (
          <div className='import-notes rounded-lg border border-amber-600/30 bg-amber-500/5 p-4'>
            <h3 className='flex items-center gap-2 text-sm font-medium'>
              <AlertTriangle aria-hidden='true' className='size-4' />
              Review before importing
            </h3>
            <ul className='mt-2 list-disc space-y-1 pl-5 text-sm text-muted-foreground'>
              {preview.warnings.map(warning => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export function ImportPreviewForm({
  action,
  initialUrl = '',
  loading,
  onPreview,
  preview,
  starting = false,
}) {
  const [url, setUrl] = useState(initialUrl)
  const [error, setError] = useState(null)
  const [checkedUrl, setCheckedUrl] = useState(preview ? initialUrl : null)
  const checked = Boolean(preview && checkedUrl === url.trim())
  const busy = loading || starting

  useEffect(() => {
    if (initialUrl) setUrl(initialUrl)
  }, [initialUrl])

  async function submit(event) {
    event.preventDefault()
    if (busy) return
    setError(null)
    setCheckedUrl(null)

    try {
      const submittedUrl = url.trim()
      await onPreview(submittedUrl)
      setCheckedUrl(submittedUrl)
    } catch (caught) {
      setError(caught.message || 'Unable to preview this feed. Try again.')
    }
  }

  return (
    <div className='import-form space-y-6'>
      <Card className='import-feed-card'>
        <CardHeader>
          <CardTitle as='h2'>Import your RSS feed</CardTitle>
          <CardDescription>
            Enter the feed from your current host. Mono checks what can be
            imported without changing the original feed.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className='space-y-5' onSubmit={submit}>
            {error && <ErrorAlert>{error}</ErrorAlert>}
            <Field>
              <FieldLabel htmlFor='import-url'>Current RSS feed URL</FieldLabel>
              <Input
                aria-describedby='import-url-description'
                autoComplete='url'
                disabled={busy}
                id='import-url'
                maxLength='2048'
                onChange={event => {
                  setUrl(event.target.value)
                  setError(null)
                }}
                placeholder='https://example.com/feed.xml'
                required
                type='url'
                value={url}
              />
              <FieldDescription id='import-url-description'>
                The feed must be publicly reachable over HTTPS.
              </FieldDescription>
            </Field>
            <Button disabled={busy} loading={loading} type='submit'>
              Check feed
            </Button>
            {loading && (
              <p className='import-inline-status' role='status'>
                <LoaderCircle
                  aria-hidden='true'
                  className='size-4 animate-spin motion-reduce:animate-none'
                />
                Checking your feed…
              </p>
            )}
          </form>
        </CardContent>
      </Card>

      {preview && checkedUrl !== null && !checked && (
        <p className='text-sm text-muted-foreground' role='status'>
          Feed URL changed. Check this feed before importing.
        </p>
      )}
      {checked && (
        <p className='text-sm text-muted-foreground' role='status'>
          Feed checked. Review the show and any notes below.
        </p>
      )}
      {checked && <ImportPreview preview={preview} />}
      {checked && action?.(checkedUrl)}
    </div>
  )
}

export function PublicImportAction({ url }) {
  const returnTo = importReturnPath(url)

  return (
    <Card className='border-foreground/20 bg-muted/30'>
      <CardContent className='flex flex-col gap-5 p-6 sm:flex-row sm:items-center sm:justify-between'>
        <div>
          <h2 className='font-semibold'>Ready to bring this show to Mono?</h2>
          <p className='mt-1 text-sm text-muted-foreground'>
            Create an account to review and start importing your podcast.
          </p>
        </div>
        <div className='flex shrink-0 flex-wrap gap-2'>
          <Button asChild>
            <Link href={`/signup?next=${encodeURIComponent(returnTo)}`}>
              Create account
              <ArrowRight aria-hidden='true' />
            </Link>
          </Button>
          <Button asChild variant='outline'>
            <Link href={`/login?next=${encodeURIComponent(returnTo)}`}>
              Log in
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
